/**
 * "Invite friends to Sportsmet" (ISSUE-1349, ISSUE-1404): v1's Refer a friend
 * and Settings > Invite friends. Shares the Sportsmet link itself, not any one
 * pool, through the phone's share sheet, or copies it where there is none.
 * It sits on the Invite tab and on Account.
 *
 * The share sheet gets the line and the link as separate fields, so targets
 * that join text and url do not print the link twice. If there is no share
 * sheet and the clipboard refuses too, the link is shown as text to copy by
 * hand, rather than the tap doing nothing (QA round 1).
 */
import { Button, SavedNote } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

export const SPORTSMET_SHARE_URL = "https://sportsmet.com";
export const INVITE_FRIENDS_TITLE = "Invite friends to Sportsmet";
export const INVITE_FRIENDS_BODY =
  "Pools are better with more people. Send your friends the app and start one together.";
export const INVITE_FRIENDS_ACTION = "Invite friends";
export const INVITE_FRIENDS_COPIED = "Link copied. Paste it in a text.";
export const INVITE_FRIENDS_SHARE_TEXT =
  "I'm running my sports pools on Sportsmet. Come play with me.";
export const INVITE_FRIENDS_MESSAGE = `I'm running my sports pools on Sportsmet. Come play with me: ${SPORTSMET_SHARE_URL}`;
export const INVITE_FRIENDS_COPY_BY_HAND =
  "Copy this link and send it to your friends:";

export type typeInviteFriendsCardProps = { testId?: string | undefined };

export const InviteFriendsCard = (
  props: typeInviteFriendsCardProps,
): ReactNode => {
  const [hasCopied, setHasCopied] = useState(false);
  const [hasCopyFailed, setHasCopyFailed] = useState(false);

  async function handleShare(): Promise<void> {
    setHasCopied(false);
    setHasCopyFailed(false);
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: INVITE_FRIENDS_TITLE,
          text: INVITE_FRIENDS_SHARE_TEXT,
          url: SPORTSMET_SHARE_URL,
        });
      } catch {
        // Closing the share sheet rejects. That is the fan's choice.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(INVITE_FRIENDS_MESSAGE);
      setHasCopied(true);
    } catch {
      // No share sheet and no clipboard: show the link to copy by hand.
      setHasCopyFailed(true);
    }
  }

  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-4"
      data-testid={props.testId ?? "invite-friends"}
    >
      <h2 className="text-h2 text-ink">{INVITE_FRIENDS_TITLE}</h2>
      <p className="text-body text-ink-secondary">{INVITE_FRIENDS_BODY}</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          onClick={() => void handleShare()}
          data-testid="invite-friends-share"
        >
          {INVITE_FRIENDS_ACTION}
        </Button>
        <SavedNote saved={hasCopied} label={INVITE_FRIENDS_COPIED} />
      </div>
      {hasCopyFailed ? (
        <p
          className="text-body text-ink-secondary"
          role="status"
          data-testid="invite-friends-link"
        >
          {INVITE_FRIENDS_COPY_BY_HAND}{" "}
          <span className="select-all break-all text-body-strong text-ink">
            {SPORTSMET_SHARE_URL}
          </span>
        </p>
      ) : null}
    </section>
  );
};
